import { useCallback, useEffect, useRef, useState } from 'react';

// Local UI state only — which item's detail sheet is open, and which
// category is currently in view for the nav's active-tab highlight. No
// data fetching happens here; this hook is pure client-side presentation
// state shared by both the admin preview and the public renderer.
export function useItemSelectionState(categories, initialCategoryId) {
  const [selectedItem, setSelectedItem] = useState(null);
  const [activeCategoryId, setActiveCategoryId] = useState(initialCategoryId ?? categories[0]?.id ?? null);
  const observerRef = useRef(null);

  const openItem = useCallback((item) => setSelectedItem(item), []);
  const closeItem = useCallback(() => setSelectedItem(null), []);

  const scrollToCategory = useCallback((categoryId) => {
    setActiveCategoryId(categoryId);
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    document.getElementById(`category-${categoryId}`)?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
  }, []);

  useEffect(() => {
    if (!initialCategoryId) return;
    const category = document.getElementById(`category-${initialCategoryId}`);
    if (!category) return;

    // Scroll this menu's browsing context only. scrollIntoView() can also
    // move ancestor documents, which would pull the landing page away from
    // its hero when the menu is embedded in the phone preview.
    const navHeight = document.querySelector('.rm-nav')?.getBoundingClientRect().height ?? 0;
    const categoryTop = category.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({ top: Math.max(0, categoryTop - navHeight), behavior: 'auto' });
  }, [initialCategoryId]);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;

    observerRef.current?.disconnect();
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) {
          const id = visible[0].target.id.replace('category-', '');
          setActiveCategoryId(id);
        }
      },
      { rootMargin: '-24px 0px -70% 0px' },
    );
    observerRef.current = observer;

    for (const category of categories) {
      const el = document.getElementById(`category-${category.id}`);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [categories]);

  return { selectedItem, openItem, closeItem, activeCategoryId, scrollToCategory };
}
